import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle, Clock, User, Shield, Calendar, XCircle, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface AdminRequest {
  id: string;
  user_id: string;
  requested_at: string;
  status: string;
  user_email?: string;
  user_name?: string;
}

export default function AdminApproval() {
  const [requests, setRequests] = useState<AdminRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<AdminRequest | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('admin_requests')
        .select('*')
        .eq('status', 'pending')
        .order('requested_at', { ascending: false });

      if (error) throw error;

      // Fetch user details for each request
      const requestsWithUsers = await Promise.all(
        (data || []).map(async (request) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('full_name, username')
            .eq('id', request.user_id)
            .single();

          // Get email via admin RPC
          const { data: email } = await supabase
            .rpc('admin_get_user_email', { p_user_id: request.user_id });

          return {
            ...request,
            user_email: email || 'Unknown',
            user_name: profile?.full_name || profile?.username || 'Unknown User',
          };
        })
      );

      setRequests(requestsWithUsers);
    } catch (error) {
      console.error('Error fetching admin requests:', error);
      toast({
        title: 'Error',
        description: 'Failed to load admin requests',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const approveRequest = async (requestId: string) => {
    setProcessing(requestId);
    try {
      const { error } = await supabase.rpc('approve_admin_request', {
        p_request_id: requestId,
      });

      if (error) throw error;

      toast({
        title: 'Admin Approved!',
        description: 'The user has been granted admin privileges.',
      });

      fetchRequests();
    } catch (error: any) {
      console.error('Error approving admin:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to approve admin request',
        variant: 'destructive',
      });
    } finally {
      setProcessing(null);
    }
  };

  const openRejectDialog = (request: AdminRequest) => {
    setSelectedRequest(request);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const rejectRequest = async () => {
    if (!selectedRequest) return;

    setProcessing(selectedRequest.id);
    setRejectDialogOpen(false);

    try {
      const { error } = await supabase.rpc('reject_admin_request', {
        p_request_id: selectedRequest.id,
        p_reason: rejectionReason || null,
      });

      if (error) throw error;

      toast({
        title: 'Request Rejected',
        description: 'The admin request has been rejected.',
      });

      fetchRequests();
    } catch (error: any) {
      console.error('Error rejecting admin:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to reject admin request',
        variant: 'destructive',
      });
    } finally {
      setProcessing(null);
      setSelectedRequest(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-muted animate-pulse rounded" />
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="space-y-3">
                  <div className="h-6 bg-muted animate-pulse rounded w-1/3" />
                  <div className="h-4 bg-muted animate-pulse rounded w-2/3" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6" />
            Admin Approvals
          </h2>
          <p className="text-muted-foreground">
            Review and approve pending admin access requests
          </p>
        </div>
        <Badge variant="secondary" className="text-lg px-3 py-1">
          {requests.length} Pending
        </Badge>
      </div>

      <Card className="border-amber-500/50 bg-amber-500/5">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
          <div>
            <p className="font-medium text-amber-700 dark:text-amber-400">Security Notice</p>
            <p className="text-sm text-muted-foreground">
              Granting admin access gives full platform control including user management, 
              financial oversight, and system settings. Only approve trusted individuals.
            </p>
          </div>
        </CardContent>
      </Card>

      {requests.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CheckCircle className="h-12 w-12 text-green-500 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Pending Requests</h3>
            <p className="text-muted-foreground text-center">
              There are no pending admin access requests at the moment.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {requests.map((request) => (
            <Card key={request.id} className="border-l-4 border-l-amber-500">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="h-5 w-5" />
                      Admin Access Request
                    </CardTitle>
                    <CardDescription className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      {request.user_name}
                      {request.user_email && ` • ${request.user_email}`}
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className="text-amber-600">
                    <Clock className="h-3 w-3 mr-1" />
                    Pending
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  Requested: {format(new Date(request.requested_at), 'MMM dd, yyyy HH:mm')}
                </div>

                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    onClick={() => approveRequest(request.id)}
                    disabled={processing === request.id}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    {processing === request.id ? (
                      <>
                        <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Approve Admin
                      </>
                    )}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => openRejectDialog(request)}
                    disabled={processing === request.id}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject Admin Request</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to reject this admin access request? 
              You can optionally provide a reason.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            placeholder="Rejection reason (optional)"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={rejectRequest} className="bg-destructive hover:bg-destructive/90">
              Reject Request
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
